// MaterialGraphWindow — the node-graph editor for one material asset.
//
// A FloatingWindow, not the fullscreen modal it used to be, and the difference
// is functional rather than cosmetic. As `position: fixed; inset: 0` it COVERED
// the content browser, so dragging a texture from the browser onto a Texture
// Sample node was impossible — not unimplemented, impossible: there was
// nothing on screen to drag from. It also had a hand-rolled Undock button that
// booted a SECOND copy of the whole app at ?material_popup=<id>, with its own
// module singletons and its own asset manager, to show one editor on another
// monitor. FloatingWindow's pop-out is a PORTAL of this same instance, so the
// second boot — and the window.open unreliability that came with it — is gone.
//
// The file name is unchanged so the import sites and the existing specs stay
// put; the export is the same component it always was.
//
// Opened by double-clicking a material asset or "Open Material Graph" in the
// inspector, both via materialGraphStore. Persists through CustomAssetManager.

import React, { useEffect, useRef, useState } from 'react'
import MaterialGraphEditor from './MaterialGraphEditor'
import { CustomAssetManager } from '../../lib/CustomAssetManager'
import { captureMaterialThumbnail } from '../../lib/materialThumbnail'
import { reloadMaterialsEverywhere } from '../../lib/materialReloadBus'
import { getCSSVars } from './materialGraphTheme'
import FloatingWindow from '../FloatingWindow.jsx'
import './materialGraph.css'

// Save-status labels. Without visible feedback, a graph edit (especially a
// surface-model swap, which rebuilds the whole material) persists + recompiles
// silently — it looks broken until the avatar suddenly changes. The ribbon
// surfaces the persist→apply lifecycle: Saving… (server PATCH) → Applying…
// (avatar rebuild + shader recompile) → Saved, or a red error.
const SAVE_LABEL = { saving: 'Saving material…', applying: 'Applying to avatar…', saved: 'Saved', error: 'Save failed' }

// Full-width status ribbon across the top of the editor — deliberately obvious
// (a small badge was too easy to miss). Accent while busy with an indeterminate
// progress stripe, green on Saved, red on error. Auto-dismissed by the caller.
function SaveRibbon({ state, error }) {
  const busy = state === 'saving' || state === 'applying'
  const tone = state === 'error' ? 'error' : state === 'saved' ? 'ok' : 'busy'
  const label = state === 'error' ? `Save failed${error ? ': ' + error : ''}` : SAVE_LABEL[state]
  return <Ribbon tone={tone} label={label} busy={busy} />
}

// The ribbon itself, kept generic: save is not necessarily the only thing that
// will need to speak inside this editor.
function Ribbon({ tone, label, busy }) {
  const bg = tone === 'error' ? '#b3261e' : tone === 'ok' ? '#15803d' : 'var(--accent, #6b4dff)'
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'absolute', top: 0, left: 0, right: 0, zIndex: 30, height: 34,
        display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px',
        color: '#fff', fontSize: 12.5, fontWeight: 600, letterSpacing: '.02em',
        background: bg, boxShadow: '0 2px 14px rgba(0,0,0,.45)', overflow: 'hidden',
        animation: 'sg-ribbon-in .18s ease-out',
      }}
    >
      {busy && <span style={{ width: 13, height: 13, borderRadius: '50%', flexShrink: 0, border: '2px solid #ffffff55', borderTopColor: '#fff', animation: 'sg-ribbon-spin .8s linear infinite' }} />}
      {tone === 'ok' && <span aria-hidden style={{ fontSize: 15 }}>✓</span>}
      {tone === 'error' && <span aria-hidden style={{ fontSize: 15, fontWeight: 800 }}>!</span>}
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      {busy && (
        <span style={{ position: 'absolute', left: 0, bottom: 0, height: 3, width: '100%', background: '#ffffff22' }}>
          <span style={{ position: 'absolute', height: '100%', width: '40%', background: '#fff', animation: 'sg-ribbon-progress 1.1s ease-in-out infinite' }} />
        </span>
      )}
      <style>{`
        @keyframes sg-ribbon-in { from { transform: translateY(-100%) } to { transform: translateY(0) } }
        @keyframes sg-ribbon-spin { to { transform: rotate(360deg) } }
        @keyframes sg-ribbon-progress { 0% { left: -40% } 100% { left: 100% } }
      `}</style>
    </div>
  )
}

export default function MaterialGraphModal({ asset, onClose }) {
  // Read the LIVE material from the manager, never the (possibly stale) `asset`
  // prop snapshot. Opening from a stale graph and then persisting it would
  // clobber newer edits — wiping nodes back to an old state. This is the same
  // stale-`asset.data` root cause behind the "exposed params need F5" bug.
  const liveMaterial = () => CustomAssetManager.listMaterials().find(m => m.id === asset.rawId)
  const [currentGraph, setCurrentGraph] = useState(() => liveMaterial()?.data?.graph ?? asset.data?.graph)
  const [zoom, setZoom] = useState(1) // Track zoom level from ReactFlow
  const [saveState, setSaveState] = useState('clean') // clean|saving|applying|saved|error
  const [saveError, setSaveError] = useState(null)
  const savedTimer = useRef(null)

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  // Clear the transient "Saved" tick timer on unmount.
  useEffect(() => () => { if (savedTimer.current) clearTimeout(savedTimer.current) }, [])


  const persist = async (graph) => {
    setCurrentGraph(graph)
    // Merge into the freshest material data, not the stale prop closure.
    const base = liveMaterial()?.data ?? asset.data ?? {}
    const nextData = { ...base, graph }
    if (savedTimer.current) { clearTimeout(savedTimer.current); savedTimer.current = null }
    setSaveError(null)
    setSaveState('saving')       // server PATCH in flight
    try {
      await CustomAssetManager.updateMaterial(asset.rawId, { data: nextData })
      setSaveState('applying')   // avatar rebuild + shader recompile
      await reloadMaterialsEverywhere()
      setSaveState('saved')
      savedTimer.current = setTimeout(() => setSaveState('clean'), 1200)
    } catch (e) {
      setSaveError(e?.message ?? String(e))
      setSaveState('error')
    }
  }

  // On commit (Apply / close), snapshot the live preview's real render into
  // data.thumbnail so the content-browser card shows what the material actually
  // looks like — the CSS-sphere fallback can't represent the compiled graph.
  // Best-effort: a failed capture (compile error, no GL) just closes without
  // touching the thumbnail, leaving the prior snapshot in place.
  const commitAndClose = async () => {
    // Awaited, not fire-and-forget: the capture is a GPU readback now. The modal
    // still closes on the same click — `onClose()` runs below regardless of what
    // the capture returns, exactly as it did when this was synchronous.
    const thumb = await captureMaterialThumbnail()
    if (thumb) {
      const base = liveMaterial()?.data ?? asset.data ?? {}
      // Fire-and-forget: updateMaterial notifies subscribers, so the card
      // re-renders with the new thumbnail without blocking the close.
      CustomAssetManager.updateMaterial(asset.rawId, { data: { ...base, thumbnail: thumb } })
    }
    onClose()
  }

  // CSS variables from theme
  const cssVars = getCSSVars()



  return (
    <FloatingWindow
      id="material-graph"
      title="MATERIAL GRAPH"
      onClose={commitAndClose}
      dockable={false}
      // Deliberately not the whole screen. The editor needs room, but a
      // default that fills the viewport reproduces the takeover this
      // conversion exists to remove — the content browser has to be
      // reachable beside it for a texture to be dragged out of. On a small
      // display it will still overlap the bottom dock; it is movable and
      // resizable now, and the geometry persists once the user sets it.
      defaultSize={{ w: 1080, h: 620 }}
      barExtra={<span className="sg-crumb">/ materials / <b>{asset.name}</b></span>}
    >
      {/* The editor's own chrome stays INSIDE the window body rather than being
          crammed into the title bar: the tab row and the zoom read are part of
          the editor, and the bar already owns float / pop-out / close. */}
      <div className="sg-root" style={{ height: '100%', ...cssVars }}>
        {/* Tabs, zoom read, Revert / Apply. Close and pop-out live on the
            window's own title bar now. */}
        <div className="sg-topbar">

          {/* Tabs */}
          <div className="sg-top-tabs">
            <button className="sg-tab is-active">Shader</button>
            <button className="sg-tab" disabled>Preview</button>
            <button className="sg-tab" disabled>Code</button>
          </div>

          {/* Right side: zoom read, buttons, close */}
          <div className="sg-top-right">
            <span className="sg-zoom-read">{Math.round(zoom * 100)}%</span>
            <button
              className="sg-btn-ghost"
              onClick={() => setCurrentGraph(asset.data?.graph)}
              title="Reset to saved version"
            >
              Revert
            </button>
            <button
              className="sg-btn-accent"
              onClick={commitAndClose}
              title="Close editor (changes auto-saved)"
            >
              Apply
            </button>
          </div>
        </div>

        {/* Editor area (3-column layout is inside MaterialGraphEditor) */}
        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {saveState !== 'clean' && <SaveRibbon state={saveState} error={saveError} />}
          <MaterialGraphEditor
            key={asset.rawId}
            graph={currentGraph}
            onChange={persist}
            onZoomChange={setZoom}
          />
          </div>
      </div>
    </FloatingWindow>
  )
}
